<template>
  <div class="card border mb-3">
    <div
      class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2"
    >
      <h5 class="mb-0">{{ $t("forwards.deals.pap.monitoring_title") }}</h5>
    </div>

    <div class="card-body">
      <LoadingIndicatorComponent
        v-if="loading"
        inline
        :message="$t('forwards.deals.pap.monitoring_loading')"
      />

      <p v-else-if="months.length === 0" class="text-body-tertiary mb-0">
        {{ $t("forwards.deals.pap.monitoring_empty") }}
      </p>

      <table v-else class="table table-sm mb-0">
        <thead>
          <tr>
            <th scope="col">{{ $t("forwards.deals.pap.month") }}</th>
            <th scope="col">
              {{ $t("forwards.deals.pap.nominated_vs_escrowed") }}
            </th>
            <th scope="col" class="text-end">
              {{ $t("forwards.deals.pap.delta") }}
            </th>
            <th scope="col">{{ $t("forwards.deals.pap.status") }}</th>
            <th scope="col" class="text-end">
              {{ $t("forwards.deals.pap.sell_price") }}
            </th>
            <th scope="col" class="text-end"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="month in months" :key="month.month">
            <td class="text-nowrap">{{ formatMonthYear(month.month) }}</td>
            <td class="fill-cell">
              <EscrowFillBarComponent
                :escrowed="month.escrowed_mwh"
                :total="barTotal(month)"
                :caption="fillCaption(month)"
                :showShortfall="month.escrowed_mwh < month.target_mwh"
              />
            </td>
            <td class="text-end text-nowrap">
              {{ deltaText(month) }}
            </td>
            <td>
              <span class="badge rounded-pill" :class="statusClass(month)">
                {{ $t(`forwards.deals.pap.status_${month.status}`) }}
              </span>
            </td>
            <td class="text-end text-nowrap" data-testid="sell-price">
              {{ priceText(month) }}
            </td>
            <td class="text-end">
              <button
                v-if="!isVoided(month)"
                type="button"
                class="btn btn-sm btn-ghost"
                @click="emit('producer-prices', month.delivery_id)"
              >
                {{ $t("forwards.deals.producer_prices.button") }}
              </button>
              <template v-if="managesDeal">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  :disabled="!month.deliverable"
                  @click="startTransfer(month)"
                >
                  {{ $t("forwards.deals.plan.deliver") }}
                </button>
                <span
                  v-if="blockedReason(month)"
                  class="text-body-tertiary fs-10 d-block"
                  >{{ blockedReason(month) }}</span
                >
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ConfirmationModalComponent
      :visible="unfinishedMonth !== null"
      :title="$t('forwards.deals.pap.ship_early_title')"
      :message="$t('forwards.deals.pap.ship_early_body')"
      :confirmButtonText="$t('forwards.deals.pap.ship_early_confirm')"
      confirmButtonClass="btn-warning"
      @confirm="shipUnfinishedMonth"
      @cancel="unfinishedMonth = null"
    />

    <TransferModalComponent
      v-if="transferTransactionId !== null"
      :visible="transferTransactionId !== null"
      :transactionId="transferTransactionId"
      @confirm="onTransferred"
      @cancel="transferTransactionId = null"
    />
  </div>
</template>

<script setup lang="ts">
import ConfirmationModalComponent from "@/components/common/ConfirmationModalComponent.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import {
  formatMonthYear,
  formatNumberWithSpaces,
} from "@/composables/formatUtils";
import EscrowFillBarComponent from "@/forwards2/components/EscrowFillBarComponent.vue";
import type { DealResponse, PapMonthStatus } from "@/forwards2/forwardsTypes";
import { PERCENT } from "@/forwards2/percentUtils";
import { priceRangeLabel } from "@/forwards2/priceUtils";
import TransferModalComponent from "@/money/admin/transactions/TransferModalComponent.vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

/**
 * How a pay-as-produced deal is actually running, month by month, and what each month sells at.
 *
 * `deliverable` is the backend's own gate, not a re-derivation of the status: a month can be
 * outside the tolerance band and still shippable, and a month inside it can be blocked because its
 * issuance window has not passed yet.
 *
 * Each month is one delivery, so its sell price is that delivery's price, read off the deal and
 * shown to every reader of it as the fixed-volume delivery card shows it — next to the month's
 * producer prices. It is set on the nominated schedule card, not here (user decision, 2026-09-28).
 */
const props = withDefaults(
  defineProps<{
    months: PapMonthStatus[];
    /** The deal the months belong to, which holds each month's price. */
    deal: DealResponse;
    managesDeal: boolean;
    loading?: boolean;
  }>(),
  {
    loading: false,
  }
);

const emit = defineEmits<{
  delivered: [deliveryId: number];
  "producer-prices": [deliveryId: number];
}>();

const { t } = useI18n();

const transferTransactionId = ref<number | null>(null);
const transferDeliveryId = ref<number | null>(null);
const unfinishedMonth = ref<PapMonthStatus | null>(null);

const deliveriesById = computed(
  () =>
    new Map(props.deal.deliveries.map((delivery) => [delivery.id, delivery]))
);

const priceText = (month: PapMonthStatus) => {
  const delivery = deliveriesById.value.get(month.delivery_id);
  if (delivery === undefined) return "";
  return priceRangeLabel(t, delivery.unit_price, delivery.unit_price);
};

/**
 * A month nominated at zero is a normal outcome (an untouched wizard input), and the status badge
 * calls anything that arrives against it "over" — so the bar is scaled to what is actually in hand
 * rather than rendering empty, while the caption keeps naming the real nomination (RV-pap-06).
 */
const barTotal = (month: PapMonthStatus) =>
  Math.max(month.target_mwh, month.escrowed_mwh);

const fillCaption = (month: PapMonthStatus) =>
  t("forwards.common.fill_of_total", {
    filled: formatNumberWithSpaces(month.escrowed_mwh),
    total: formatNumberWithSpaces(month.target_mwh),
  });

/**
 * Why the month cannot ship, straight from the ship gate — a fresh contract has no buyer details
 * yet, so every one of its months is blocked and the table would otherwise say nothing at all
 * (RV2-pap-lifecycle-03). Rendered exactly as `DeliveryCardComponent` renders its own gate.
 */
const blockedReason = (month: PapMonthStatus) =>
  month.deliverable ? "" : month.reasons.join(" ");

/** A voided month invoiced nothing and its producer lines are gone, so there are no prices to show. */
const isVoided = (month: PapMonthStatus) => month.transaction_status === null;

/** The month's escrow against its nomination as a signed percentage; a zero nomination has no ratio. */
const deltaText = (month: PapMonthStatus) => {
  if (month.target_mwh === 0) return "-";
  const percent =
    Math.round((month.escrowed_mwh / month.target_mwh - 1) * PERCENT * 10) / 10;
  const sign = percent > 0 ? "+" : percent < 0 ? "−" : "";
  return `${sign}${Math.abs(percent).toFixed(1)} %`;
};

const statusClass = (month: PapMonthStatus) => {
  switch (month.status) {
    case "delivered":
      return "bg-success-subtle text-success-emphasis";
    case "within":
      return "bg-primary-subtle text-primary-emphasis";
    case "over":
    case "under":
      return "bg-warning-subtle text-warning-emphasis";
    default:
      return "bg-secondary-subtle text-secondary-emphasis";
  }
};

/**
 * The ship threshold is zero (spec, FIX2-4): a month with nothing escrowed may go out as soon as
 * its transaction is ready. Shipping freezes the month, so a month still producing — or one holding
 * no escrow at all — is confirmed first: everything issued for it afterwards lands as free escrow
 * that ops has to return by hand.
 */
const needsConfirmation = (month: PapMonthStatus) =>
  month.status === "pending" || month.escrowed_mwh === 0;

const startTransfer = (month: PapMonthStatus) => {
  if (needsConfirmation(month)) {
    unfinishedMonth.value = month;
    return;
  }
  openTransfer(month);
};

const shipUnfinishedMonth = () => {
  const month = unfinishedMonth.value;
  unfinishedMonth.value = null;
  if (month !== null) openTransfer(month);
};

const openTransfer = (month: PapMonthStatus) => {
  transferTransactionId.value = month.sales_transaction_id;
  transferDeliveryId.value = month.delivery_id;
};

const onTransferred = () => {
  const deliveryId = transferDeliveryId.value;
  transferTransactionId.value = null;
  transferDeliveryId.value = null;
  if (deliveryId !== null) emit("delivered", deliveryId);
};
</script>

<style scoped lang="scss">
.fill-cell {
  min-width: 14rem;
}
</style>
